import React, { useState } from 'react';
import { UserPlus, X, Plus } from 'lucide-react';
import { createCareProvider } from '../../utils/api';
import AddressAutocomplete from './AddressAutocomplete';
import toast from 'react-hot-toast';

const EMPTY_FORM = {
  name: '', provider_type: '', specialty: '', phone: '', fax: '',
  email: '', address: '', city: '', state: '', zip: '', notes: '',
};

/**
 * AddProviderModal — shared "Add New Provider to Network" modal.
 *
 * Props:
 *   isOpen        boolean
 *   onClose       () => void
 *   onCreated     (provider) => void   — called after successful creation
 *   initialName   string               — pre-fills the Name field
 *   providerTypes string[]             — from custom lists
 *   specialties   string[]             — from custom lists (optional)
 */
export default function AddProviderModal({
  isOpen, onClose, onCreated,
  initialName = '',
  providerTypes = ['Doctor','Specialist','Physical Therapy','Chiropractor','Hospital','Lab','Imaging','Referral Source','Other'],
  specialties = [],
}) {
  const [form, setForm]     = useState({ ...EMPTY_FORM, name: initialName });
  const [saving, setSaving] = useState(false);

  // Reset form when modal opens with a new initialName
  React.useEffect(() => {
    if (isOpen) setForm({ ...EMPTY_FORM, name: initialName });
  }, [isOpen, initialName]);

  const setF = (key, val) => setForm(p => ({ ...p, [key]: val }));

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Provider name is required'); return; }
    setSaving(true);
    try {
      const newP = await createCareProvider({
        name:          form.name.trim(),
        provider_type: form.provider_type,
        specialty:     form.specialty,
        phone:         form.phone,
        fax:           form.fax,
        email:         form.email,
        address:       form.address,
        city:          form.city,
        state:         form.state,
        zip:           form.zip,
        notes:         form.notes,
      });
      toast.success(`"${newP.name}" added to Provider Network`);
      onCreated(newP);
      onClose();
    } catch (err) {
      toast.error(err?.message || 'Failed to add provider');
    } finally { setSaving(false); }
  };

  if (!isOpen) return null;

  const inputStyle = {
    width: '100%', padding: '7px 10px', fontSize: '0.85rem',
    background: 'var(--zeus-navy-light)', border: '1px solid var(--zeus-border)',
    borderRadius: 7, color: 'var(--text-primary)', outline: 'none',
    fontFamily: 'inherit', boxSizing: 'border-box',
  };
  const labelStyle = {
    fontSize: '0.72rem', color: 'var(--text-muted)', fontWeight: 700,
    display: 'block', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em',
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 9998, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.65)' }} onClick={onClose} />
      <div style={{
        position: 'relative', width: 500, maxWidth: '96vw', maxHeight: '92vh',
        background: 'var(--zeus-navy)', border: '1px solid var(--zeus-border)',
        borderRadius: 12, boxShadow: '0 20px 60px rgba(0,0,0,0.6)',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '14px 20px', borderBottom: '1px solid var(--zeus-border)',
          background: 'var(--zeus-navy-light)', flexShrink: 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <UserPlus size={16} style={{ color: 'var(--zeus-gold)' }} />
            <span style={{ fontSize: '0.95rem', fontWeight: 700, color: 'var(--text-primary)' }}>Add Provider to Network</span>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', display: 'flex', padding: 4 }}>
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '18px 20px', overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: 12 }}>

          {/* Name */}
          <div>
            <label style={labelStyle}>Provider Name *</label>
            <input autoFocus style={{ ...inputStyle, borderColor: !form.name.trim() ? 'transparent' : 'var(--zeus-border)' }}
              value={form.name} onChange={e => setF('name', e.target.value)}
              placeholder="e.g. Advanced Spine & Pain – Humble" />
          </div>

          {/* Type + Specialty */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={labelStyle}>Provider Type</label>
              <select style={inputStyle} value={form.provider_type} onChange={e => setF('provider_type', e.target.value)}>
                <option value="">— Select type —</option>
                {providerTypes.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Specialty</label>
              {specialties.length > 0 ? (
                <select style={inputStyle} value={form.specialty} onChange={e => setF('specialty', e.target.value)}>
                  <option value="">— Select specialty —</option>
                  {specialties.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              ) : (
                <input style={inputStyle} value={form.specialty} onChange={e => setF('specialty', e.target.value)}
                  placeholder="e.g. Orthopedics" />
              )}
            </div>
          </div>

          {/* Phone + Fax */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={labelStyle}>Phone</label>
              <input style={inputStyle} value={form.phone} onChange={e => setF('phone', e.target.value)} placeholder="(___) ___-____" />
            </div>
            <div>
              <label style={labelStyle}>Fax</label>
              <input style={inputStyle} value={form.fax} onChange={e => setF('fax', e.target.value)} placeholder="(___) ___-____" />
            </div>
          </div>

          {/* Email */}
          <div>
            <label style={labelStyle}>Email</label>
            <input style={inputStyle} type="email" value={form.email} onChange={e => setF('email', e.target.value)} placeholder="provider@example.com" />
          </div>

          {/* Address */}
          <div>
            <label style={labelStyle}>Street Address</label>
            <AddressAutocomplete
              value={form.address}
              onChange={(v) => setF('address', v)}
              onPlaceSelect={({ street, city, state, zip }) => {
                setForm(p => ({
                  ...p,
                  address: street,
                  city:    city  || p.city,
                  state:   state || p.state,
                  zip:     zip   || p.zip,
                }));
              }}
              placeholder="123 Main St"
              style={inputStyle}
              className=""
            />
          </div>

          {/* City / State / Zip */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 70px 80px', gap: 10 }}>
            <div>
              <label style={labelStyle}>City</label>
              <input style={inputStyle} value={form.city} onChange={e => setF('city', e.target.value)} />
            </div>
            <div>
              <label style={labelStyle}>State</label>
              <input style={inputStyle} value={form.state} onChange={e => setF('state', e.target.value)} maxLength={2} placeholder="TX" />
            </div>
            <div>
              <label style={labelStyle}>Zip</label>
              <input style={inputStyle} value={form.zip} onChange={e => setF('zip', e.target.value)} maxLength={5} />
            </div>
          </div>

          {/* Notes */}
          <div>
            <label style={labelStyle}>Notes</label>
            <textarea
              style={{ ...inputStyle, minHeight: 60, resize: 'vertical' }}
              value={form.notes} onChange={e => setF('notes', e.target.value)}
              placeholder="Optional notes about this provider..."
            />
          </div>

          {/* Info note */}
          <div style={{
            padding: '8px 12px', borderRadius: 7, fontSize: '0.8rem',
            background: 'rgba(200,168,75,0.08)', border: '1px solid rgba(200,168,75,0.2)',
            color: 'var(--zeus-gold)', lineHeight: 1.5,
          }}>
             This provider will be saved to the Provider Network and immediately available for selection.
          </div>
        </div>

        {/* Footer */}
        <div style={{
          display: 'flex', gap: 8, justifyContent: 'flex-end', padding: '12px 20px',
          borderTop: '1px solid var(--zeus-border)', background: 'rgba(0,0,0,0.1)', flexShrink: 0,
        }}>
          <button onClick={onClose} style={{
            background: 'rgba(255,255,255,0.06)', border: '1px solid var(--zeus-border)',
            color: 'var(--text-secondary)', padding: '7px 16px', borderRadius: 8,
            cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600, fontFamily: 'inherit',
          }}>Cancel</button>
          <button onClick={handleSave} disabled={saving || !form.name.trim()} style={{
            background: 'var(--zeus-gold)', border: 'none', color: '#0f1f38',
            padding: '7px 20px', borderRadius: 8, cursor: saving ? 'default' : 'pointer',
            fontSize: '0.85rem', fontWeight: 700, fontFamily: 'inherit',
            opacity: saving || !form.name.trim() ? 0.6 : 1,
            display: 'flex', alignItems: 'center', gap: 6,
          }}>
            <Plus size={14} /> {saving ? 'Adding…' : 'Add & Select'}
          </button>
        </div>
      </div>
    </div>
  );
}
